import React from "react";
import { motion } from "framer-motion";
import {
  User,
  Target,
  Zap,
  Globe,
  Award,
  Clock,
  TrendingUp,
} from "lucide-react";

const About = () => {
  const specialties = [
    {
      icon: <Zap />,
      title: "Fast Development",
      desc: "Quick turnaround times",
    },
    { icon: <Target />, title: "SEO Focused", desc: "Search engine optimized" },
    {
      icon: <Globe />,
      title: "Responsive Design",
      desc: "Works on all devices",
    },
    { icon: <Award />, title: "Quality Code", desc: "Clean and maintainable" },
  ];

  return (
    <section id="about" className="py-20 relative">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Section Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center space-x-2 px-4 py-2 rounded-full glass-effect border border-white/10 mb-4"
          >
            <User className="w-4 h-4 text-primary-400" />
            <span className="text-sm">About Me</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            Crafting Digital
            <span className="block gradient-text">Excellence</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-xl text-gray-300 max-w-3xl mx-auto"
          >
            I am a Digital Marketing Specialist with 4+ years of hands-on
            experience in SEO, lead generation, and performance-driven marketing
            strategies. I help businesses—especially real estate, construction,
            and service-based brands—improve online visibility, generate quality
            leads, and grow organically through SEO, Local SEO, and content
            optimization. My expertise includes:
            • On-page, Off-page & Technical SEO 
            • Local SEO & Google Business Profile optimization 
            • Keyword research & competitor analysis 
            • Google Analytics & Search Console 
            • Content marketing & lead funnels 
            • Social media & email marketing I
            believe in data-driven decisions, clean execution, and measurable
            results. 📍 Bengaluru | Open to opportunities & collaborations
          </motion.p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Timeline */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary-500 to-secondary-500"></div>

              {/* Timeline Items */}
              {[
                {
                  year: "2015",
                  title: "Started Career",
                  desc: "Network Engineer at Securetel",
                },
                {
                  year: "2018",
                  title: "Advanced Networking",
                  desc: "CCNP Certified Engineer",
                },
                {
                  year: "2022",
                  title: "Transition to Development",
                  desc: "Front-End Developer",
                },
                {
                  year: "2024",
                  title: "Full Stack Growth",
                  desc: "Development + Marketing",
                },
              ].map((item, index) => (
                <div key={index} className="relative pl-20 pb-8">
                  <div className="absolute left-4 w-4 h-4 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500"></div>
                  <div className="text-sm text-primary-400 font-semibold">
                    {item.year}
                  </div>
                  <div className="text-xl font-semibold mt-1">{item.title}</div>
                  <div className="text-gray-400 mt-2">{item.desc}</div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right Column - Specialties */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 gap-6"
          >
            {specialties.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="p-6 rounded-2xl glass-effect border border-white/10 group hover:border-primary-500/30 transition-all"
              >
                <div className="text-primary-400 mb-4 group-hover:scale-110 transition-transform">
                  {item.icon}
                </div>
                <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Stats Bar */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-20 p-8 rounded-3xl glass-effect border border-white/10"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold gradient-text">9+</div>
              <div className="text-gray-400 mt-2">Years Experience</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold gradient-text">20+</div>
              <div className="text-gray-400 mt-2">Projects Delivered</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold gradient-text">100%</div>
              <div className="text-gray-400 mt-2">Client Satisfaction</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold gradient-text">
                Digital Marketing
              </div>
              <div className="text-gray-400 mt-2">Certified Professional</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
